import React, { FC, useEffect, useState } from 'react';
import { AiPromptSendButton } from 'src/components/widgets/ai';
import styled, { keyframes } from 'styled-components';

export const ChatContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 4px 2px;
`;

export const ChatBubble = styled.div<{ $role: 'user' | 'assistant' }>`
    align-self: ${(props) => (props.$role === 'user' ? 'flex-end' : 'flex-start')};
    max-width: 85%;
    padding: 10px 14px;
    border-radius: 14px;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
    background: ${(props) =>
        props.$role === 'user'
            ? 'linear-gradient(135deg, rgb(124, 58, 237) 0%, rgb(168, 85, 247) 50%, rgb(236, 72, 153) 100%)'
            : props.theme.app.backgroundColorBase};
    color: ${(props) => (props.$role === 'user' ? '#fff' : props.theme.app.primary_dark)};
    border: ${(props) => (props.$role === 'user' ? 'none' : `1px solid ${props.theme.app.borderColor}`)};
`;

export const ChatInputArea = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
    padding-top: 12px;
    flex-shrink: 0;
`;

export const ChatInput = styled.textarea`
    flex: 1;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    padding: 8px 12px;
    font-family: ${(props) => props.theme.app.font};
    font-size: 13px;
    resize: vertical;
    min-height: 60px;
    line-height: 1.4;

    &:focus {
        outline: none;
        border-color: ${(props) => props.theme.ai.solid};
    }

    &::placeholder {
        color: ${(props) => props.theme.app.component.icon.button.default.color};
    }
`;

export const ChatSendButton = styled(AiPromptSendButton).attrs({ $size: 38 })<{ $disabled: boolean }>`
    ${(props) =>
        props.$disabled &&
        `
        background: ${props.theme.app.borderColor};
        cursor: not-allowed;

        &:hover:not(:disabled) {
            transform: none;
        }
    `}
`;

const chatThinkingFade = keyframes`
	0%, 100% { opacity: 0; transform: translateY(4px); }
	10%, 90% { opacity: 1; transform: translateY(0); }
`;

export const ChatThinkingBubble = styled.div`
    align-self: flex-start;
    padding: 10px 14px;
    background: ${(props) => props.theme.app.backgroundColorBase};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 14px;
    font-size: 13px;
    color: ${(props) => props.theme.app.component.icon.button.default.color};
    font-style: italic;
    animation: ${chatThinkingFade} 5s ease-in-out infinite;
`;

interface ChatThinkingIndicatorProps {
    phrases: string[];
}

export const ChatThinkingIndicator: FC<ChatThinkingIndicatorProps> = ({ phrases }) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setIndex((prev) => (prev + 1) % phrases.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [phrases.length]);

    return <ChatThinkingBubble key={index}>{phrases[index]}</ChatThinkingBubble>;
};
